// What happens when a page crashes in a visitor's browser. The error is sent to
// /api/client-error, which writes it to Netlify's logs, and the page reloads
// once, as a reload has fixed every crash seen so far. A second crash on the
// same page in the same visit shows a message instead, so it can't keep reloading.

const RELOADED_KEY = 'languagekeyboard:reloaded-after-error';

export const reportClientError = (error: unknown, componentStack?: string | null) => {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    const body = JSON.stringify({
      name: err.name,
      message: err.message,
      stack: err.stack?.slice(0, 4000),
      componentStack: componentStack?.slice(0, 2000),
      page: window.location.pathname + window.location.search,
      // Whether the offline service worker was in charge of the page
      serviceWorker: Boolean(navigator.serviceWorker?.controller),
      sinceLoad: Math.round(performance.now()),
    });
    const sent = navigator.sendBeacon?.('/api/client-error', new Blob([body], { type: 'application/json' }));
    if (!sent) {
      fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Reporting is a convenience, so carry on without it
  }
};

// Reloads the page unless it has already been reloaded after a crash in this
// visit. Returns whether it is reloading.
export const reloadOnce = (): boolean => {
  try {
    if (window.sessionStorage.getItem(RELOADED_KEY) === window.location.pathname) return false;
    window.sessionStorage.setItem(RELOADED_KEY, window.location.pathname);
  } catch {
    // Without storage there's no way to tell whether it already reloaded
    return false;
  }
  window.location.reload();
  return true;
};
